<template>  <div class="bg-white/90 rounded-lg p-3 sm:p-4 lg:p-6 shadow-lg transition-colors">
    <!-- Mobile-first layout -->
    <div class="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
      <!-- Logo and title section -->
      <div class="flex flex-col xs:flex-row xs:items-center xs:justify-center sm:justify-start gap-3 sm:gap-4">
        <!-- Logo section -->
        <router-link to="/" class="flex items-center justify-center xs:justify-start gap-2 group transition-all duration-300 hover:scale-105">
          <div class="relative flex-shrink-0">
            <img src="/ducksImages/duckIcon.webp" class="w-10 sm:w-12 h-10 sm:h-12 drop-shadow-xl group-hover:scale-110 transition-transform duration-300" alt="DuckNSeek Logo" />
          </div>
          <span class="font-bold text-lg sm:text-xl text-gray-900">DuckNSeek</span>
        </router-link>
        
        <!-- Title section with mobile-optimized text -->
        <div class="text-center xs:text-left sm:pl-4 lg:pl-6 sm:border-l-2 border-gray-200">
          <h1 class="text-base xs:text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-indigo-600 leading-tight">
            <span class="hidden sm:inline">Duck Fleet Management</span>
            <span class="sm:hidden">Fleet Manager</span>
            <span class="inline-block ml-1">🚢</span>
          </h1>
          <div v-if="username" class="mt-1 animate-pulse-slow">
            <span class="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-yellow-100 to-orange-100 border border-orange-200 rounded-full text-orange-800 text-xs sm:text-sm font-semibold shadow-sm">
              <span class="animate-bounce">👑</span>
              <span>Captain {{ username }}</span>
              <span class="animate-spin-slow">⚓</span>
            </span>
          </div>
          <p class="text-gray-600 text-xs sm:text-sm mt-1 hidden xs:block">
            <span class="hidden sm:inline">Command your rubber duck armada with style</span>
            <span class="sm:hidden">Manage your duck fleet</span>
          </p>
        </div>
      </div>      <!-- Enhanced Action Buttons -->
      <div class="flex flex-col xs:flex-row gap-2 xs:gap-3">
        <!-- Primary Action Button -->
        <v-btn
          color="primary"
          variant="elevated"
          prepend-icon="mdi-plus"
          @click="$emit('create-cruise')"
          class="elevation-3 w-full xs:w-auto group hover:shadow-lg transition-all duration-300"
          :size="$vuetify.display.xs ? 'default' : 'large'"
        >
          <span class="font-medium">New Cruise</span>
        </v-btn>

        <!-- Logout Button -->
        <v-btn
          color="error"
          variant="outlined"
          prepend-icon="mdi-logout"
          @click="$emit('logout')"
          class="w-full xs:w-auto hover:bg-error hover:text-white transition-all duration-300"
          :size="$vuetify.display.xs ? 'default' : 'large'"
        >
          <span class="font-medium">Logout</span>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  username?: string;
}>();

defineEmits(['create-cruise', 'logout']);
</script>

<style scoped>
/* Mobile-first responsive design */
@media (max-width: 475px) {
  .admin-title {
    font-size: 1rem;
    line-height: 1.3;
  }
}

@media (max-width: 375px) {
  .admin-title {
    font-size: 0.875rem;
  }
}

/* Ensure proper text wrapping */
.break-words {
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
}

/* Better touch targets for mobile */
@media (max-width: 640px) {
  .v-btn {
    min-height: 44px;
    touch-action: manipulation;
  }
}

/* Enhanced button animations */
.group:hover .group-hover\:rotate-90 {
  transform: rotate(90deg);
}

/* Fun animations for username display */
@keyframes spin-slow {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin-slow {
  animation: spin-slow 3s linear infinite;
}

.animate-pulse-slow {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Floating accent animation */
@keyframes float-accent {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-2px) scale(1.1);
  }
}

.animate-pulse {
  animation: float-accent 2s ease-in-out infinite;
}

/* Button hover effects */
.v-btn.elevation-3:hover {
  transform: translateY(-2px);
  transition: all 0.3s ease;
}

/* Enhanced button styling */
.v-btn {
  border-radius: 8px;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.v-btn.v-btn--variant-outlined:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.v-btn.v-btn--variant-elevated {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.v-btn.v-btn--variant-elevated:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

@media (prefers-reduced-motion: reduce) {
  .animate-float,
  .animate-pulse,
  .group-hover\:rotate-90,
  .hover\:scale-105:hover {
    animation: none;
    transform: none;
  }
}
</style>